import React, { useState } from 'react';
import { Plus, Trash2, ChevronUp, ChevronDown, Eye, EyeOff, RefreshCw } from 'lucide-react';
import { useBoqClauseLibrary } from '../hooks/useBoqClauseLibrary';
import { CLAUSE_KINDS, CLAUSE_LABEL } from '../hooks/useBoqClauses';
import { BrioCard, BrioButton, BrioEmptyState } from '../../components/BrioComponents';
import { ToolbarIcon } from '../components/ToolbarIcon';

// ============================================================================
// Standard Clauses — admin editor for boq.clause_library, the firm-wide
// template set of inclusions / exclusions / assumptions / qualifications that
// seed each new project's bill cover. Editing here only affects FUTURE projects
// seeded from the template: project clauses are copied (not linked) at seed
// time, so issued bills never change retroactively.
// ============================================================================

const KIND_BLURB = {
  inclusion: 'What the quoted rates are deemed to cover.',
  exclusion: 'What is not priced and falls outside the bill.',
  assumption: 'Stated bases the estimate relies on.',
  qualification: 'Caveats that condition the figures.',
};

function ClauseRow({ clause, isFirst, isLast, onSave, onToggle, onMove, onDelete }) {
  const [busy, setBusy] = useState(false);

  const wrap = (fn) => async (...args) => {
    setBusy(true);
    await fn(...args);
    setBusy(false);
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '0.5rem',
        padding: '0.5rem 0',
        borderBottom: '1px solid var(--brio-border-subtle, #eee)',
        opacity: clause.isActive ? 1 : 0.45,
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', paddingTop: 2 }}>
        <ToolbarIcon onClick={wrap(() => onMove(clause.id, -1))} disabled={isFirst || busy} tooltip="Move up">
          <ChevronUp size={14} />
        </ToolbarIcon>
        <ToolbarIcon onClick={wrap(() => onMove(clause.id, 1))} disabled={isLast || busy} tooltip="Move down">
          <ChevronDown size={14} />
        </ToolbarIcon>
      </div>
      <textarea
        defaultValue={clause.text}
        onBlur={(e) => {
          const v = e.target.value.trim();
          if (v && v !== clause.text) onSave(clause.id, v);
          else e.target.value = clause.text; // revert empty / unchanged
        }}
        rows={Math.max(1, Math.ceil(clause.text.length / 80))}
        style={{
          flex: 1,
          minHeight: 34,
          resize: 'vertical',
          padding: '0.4rem 0.5rem',
          border: '1px solid var(--brio-border, #ccc)',
          borderRadius: 4,
          fontFamily: 'Raleway, sans-serif',
          fontSize: '0.88rem',
          lineHeight: 1.4,
          color: 'var(--brio-text, #333)',
        }}
      />
      <ToolbarIcon
        onClick={wrap(() => onToggle(clause.id, !clause.isActive))}
        disabled={busy}
        tooltip={clause.isActive ? 'Active — click to hide from template' : 'Hidden — click to activate'}
        active={clause.isActive}
      >
        {clause.isActive ? <Eye size={15} /> : <EyeOff size={15} />}
      </ToolbarIcon>
      <ToolbarIcon onClick={wrap(() => onDelete(clause))} disabled={busy} danger tooltip="Delete clause">
        <Trash2 size={14} />
      </ToolbarIcon>
    </div>
  );
}

function KindSection({ kind, clauses, onAdd, onSave, onToggle, onMove, onDelete }) {
  const [draft, setDraft] = useState('');
  const [adding, setAdding] = useState(false);
  const [err, setErr] = useState(null);

  const add = async () => {
    setErr(null);
    setAdding(true);
    const res = await onAdd({ kind, text: draft });
    setAdding(false);
    if (res.success) setDraft('');
    else setErr(res.error || 'Failed to add.');
  };

  const activeCount = clauses.filter((c) => c.isActive).length;

  return (
    <BrioCard style={{ marginBottom: '1rem' }}>
      <div style={{ marginBottom: '0.75rem' }}>
        <h3 style={{ margin: 0, fontFamily: 'Raleway, sans-serif', fontSize: '1.05rem' }}>
          {CLAUSE_LABEL[kind]}{' '}
          <span style={{ color: 'var(--brio-text-muted)', fontWeight: 400, fontSize: '0.85rem' }}>
            · {activeCount} active{clauses.length !== activeCount ? ` / ${clauses.length} total` : ''}
          </span>
        </h3>
        <div style={{ fontSize: '0.8rem', color: 'var(--brio-text-muted)', marginTop: '0.2rem' }}>
          {KIND_BLURB[kind]}
        </div>
      </div>

      {clauses.length === 0 ? (
        <BrioEmptyState>No standard {CLAUSE_LABEL[kind].toLowerCase()} yet.</BrioEmptyState>
      ) : (
        clauses.map((c, i) => (
          <ClauseRow
            key={c.id}
            clause={c}
            isFirst={i === 0}
            isLast={i === clauses.length - 1}
            onSave={onSave}
            onToggle={onToggle}
            onMove={onMove}
            onDelete={onDelete}
          />
        ))
      )}

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', marginTop: '0.75rem' }}>
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) add();
          }}
          placeholder={`Add a standard ${kind}… (⌘/Ctrl+Enter to save)`}
          rows={2}
          style={{
            flex: 1,
            resize: 'vertical',
            padding: '0.4rem 0.5rem',
            border: '1px solid var(--brio-border, #ccc)',
            borderRadius: 4,
            fontFamily: 'Raleway, sans-serif',
            fontSize: '0.88rem',
            lineHeight: 1.4,
            color: 'var(--brio-text, #333)',
          }}
        />
        <BrioButton
          variant="primary"
          onClick={add}
          disabled={adding || !draft.trim()}
          style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', padding: '0.4rem 0.8rem', whiteSpace: 'nowrap' }}
        >
          <Plus size={14} /> {adding ? 'Adding…' : 'Add'}
        </BrioButton>
      </div>
      {err && <div style={{ color: '#a94442', marginTop: '0.5rem', fontSize: '0.82rem' }}>{err}</div>}
    </BrioCard>
  );
}

function ClauseLibraryPage() {
  const { byKind, loading, error, refresh, create, update, remove, move } = useBoqClauseLibrary();

  const onSave = (id, text) => update(id, { text });
  const onToggle = (id, isActive) => update(id, { isActive });
  const onDelete = (clause) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete this ${clause.kind} from the standard library?\n\n“${clause.text}”`)) return Promise.resolve();
    return remove(clause.id);
  };

  if (loading) {
    return <div style={{ padding: '2rem', color: 'var(--brio-text-muted)' }}>Loading standard clauses…</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', marginBottom: '1rem' }}>
        <div style={{ fontSize: '0.85rem', color: 'var(--brio-text-muted)', maxWidth: 820 }}>
          The firm-wide template that seeds each new project's bill cover. Editing here affects
          <strong> only future projects</strong> — clauses are copied (not linked) when a project is created, so issued
          bills never change retroactively. Tokens like <code>{'{date}'}</code>, <code>{'{stage}'}</code>,{' '}
          <code>{'{city}'}</code>, <code>{'{consultant}'}</code> and <code>{'{tolerance}'}</code> are filled in
          automatically at seed time. Hidden clauses (eye-off) stay in the library but aren't copied into new projects.
        </div>
        <ToolbarIcon onClick={refresh} tooltip="Refresh"><RefreshCw size={15} /></ToolbarIcon>
      </div>

      {error && <div style={{ color: '#a94442', marginBottom: '0.75rem' }}>{error}</div>}

      {CLAUSE_KINDS.map((kind) => (
        <KindSection
          key={kind}
          kind={kind}
          clauses={byKind.get(kind) || []}
          onAdd={create}
          onSave={onSave}
          onToggle={onToggle}
          onMove={move}
          onDelete={onDelete}
        />
      ))}
    </div>
  );
}

export default ClauseLibraryPage;
